import React, { useEffect, useRef, useState } from 'react'
import CodeMirror, {
  EditorState,
  EditorView,
  ReactCodeMirrorRef,
} from '@uiw/react-codemirror'
import { xml } from '@codemirror/lang-xml'
import { vscodeLight, vscodeDark } from '@uiw/codemirror-theme-vscode'
import { useTheme } from '@config/themeContext'
import { indentRange } from '@codemirror/language'
import { highlightSelectionMatches } from '@codemirror/search'

interface XmlEditorProps {
  jobId?: string | undefined
  value: string
  onChange: (event: string) => void
  [key: string]: any
}

const XmlEditor: React.FC<XmlEditorProps> = ({ jobId, value, onChange, ...props }) => {
  const { theme } = useTheme()
  const editorRef = useRef<ReactCodeMirrorRef>(null)
  const [isReindented, setIsReindented] = useState<boolean>(false)

  useEffect(() => {
    if (jobId) {
      setIsReindented(false) // Allow initial reindentation when job in editor has changed
    }
  }, [jobId])

  useEffect(() => {
    if (!isReindented) {
      reindentEditor()
    }
  }, [isReindented, value])

  useEffect(() => {
    const val = getEditorContent()
    if (val && checkReindentationNeed(val)) {
      setIsReindented(false)
    }
  }, [editorRef.current, value])

  const checkReindentationNeed = (value: string) => {
    const lines = value.split('\n')
    for (const line of lines) {
      if (
        line.startsWith(' ') ||
        line.startsWith('\t') ||
        line.startsWith('&#9;')
      ) {
        return false
      }
    }
    return true
  }

  const reindentEditor = () => {
    if (editorRef.current && editorRef.current.view) {
      const editorView = editorRef.current.view
      const state = editorView.state

      const from = 0
      const to = state.doc.length

      // Get the indent effects for the whole document
      const indentEffects = indentRange(state, from, to)

      // Create and dispatch a transaction to apply the indent effects
      const transaction = state.update({
        changes: indentEffects,
      })

      editorView.dispatch(transaction)
      setIsReindented(true)
    }
  }

  const handleEditorChange = (value: string) => {
    const val = getEditorContent()
    onChange(val)
  }

  const getEditorContent = () => {
    if (editorRef.current && editorRef.current.view) {
      const editorView = editorRef.current.view
      return editorView.state.doc.toString()
    }
    return ''
  }

  return (
    <CodeMirror
      ref={editorRef}
      value={value}
      onChange={handleEditorChange}
      extensions={[
        xml(),
        EditorView.lineWrapping,
        highlightSelectionMatches({ maxMatches: 0 }),
      ]}
      theme={theme === 'dark' ? vscodeDark : vscodeLight}
      {...props}
    />
  )
}

XmlEditor.defaultProps = {
  value: '',
  width: '100%',
  height: '100%',
}

export default XmlEditor
